import React, { useState, useEffect, useCallback } from 'react';
import { useLanguage } from '../context/LanguageContext';
import Link from 'next/link';
import Image from 'next/image';
import { convertLegacyUrl } from '@/lib/i18n-config';
import { ChevronUp, Phone, Mail, MapPin } from 'lucide-react';
import { getGoogleMapsDirectionsUrl, TANNENBODEN_ADDRESS } from '@/utils/mapUtils';

const Footer = () => {
  const [showToTop, setShowToTop] = useState(false);
  const { translate, language } = useLanguage();

  // Helper function to get translated label - similar to Navigation component
  const getTranslatedItem = useCallback((key: string, defaultValue: string): string => {
    return translate(key, defaultValue);
  }, [translate]);

  // Footer translation keys with German defaults
  const footerItems = {
    contact: {
      key: 'footer.contact',
      default: 'Kontakt'
    },
    quickLinks: {
      key: 'footer.quickLinks',
      default: 'Schnelllinks'
    },
    activities: {
      key: 'footer.activities',
      default: 'Aktivitäten'
    },
    followUs: {
      key: 'footer.followUs',
      default: 'Folgen Sie uns'
    },
    call: {
      key: 'footer.call',
      default: 'Anrufen'
    },
    email: {
      key: 'footer.email',
      default: 'E-Mail'
    },
    home: {
      key: 'footer.home',
      default: 'Startseite'
    },
    chalet: {
      key: 'footer.chalet',
      default: 'Chalet'
    },
    book: {
      key: 'footer.book',
      default: 'Buchen'
    },
    availability: {
      key: 'footer.availability',
      default: 'Verfügbarkeit'
    },
    attractions: {
      key: 'footer.attractions',
      default: 'Sehenswürdigkeiten'
    },
    summerActivities: {
      key: 'footer.summerActivities',
      default: 'Sommeraktivitäten'
    },
    skiing: {
      key: 'footer.skiing',
      default: 'Skifahren & Snowboarden'
    },
    familyAdventures: {
      key: 'footer.familyAdventures',
      default: 'Familienausflüge'
    },
    copyright: {
      key: 'footer.copyright',
      default: '© {year} Tannenboden Chalet Engelberg'
    }
  };

  // Back to top with debounced scroll listener
  useEffect(() => {
    let scrollTimer: NodeJS.Timeout;
    const handleScroll = () => {
      clearTimeout(scrollTimer);
      scrollTimer = setTimeout(() => {
        setShowToTop(window.scrollY > 500);
      }, 100);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      clearTimeout(scrollTimer);
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return (
    <>
      {/* Mobile-optimized back to top - larger touch target */}
      {showToTop && (
        <button
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          className="fixed bottom-6 right-6 bg-chalet-gold-400 text-alpine-blue-900 p-5 rounded-full shadow-md z-50"
        >
          <ChevronUp className="h-6 w-6" />
        </button>
      )}

      {/* Mobile-first footer */}
      <footer className="bg-alpine-blue-800 text-white border-t-2 border-chalet-gold-400/40">
        {/* Mobile view - super minimalistic */}
        <div className="md:hidden py-6 px-4">
          {/* Simplified contact links with large touch targets */}
          <div className="flex justify-center gap-6 mb-6">
            <a href="tel:+41787460447" className="flex flex-col items-center">
              <div className="bg-chalet-gold-400/20 p-4 rounded-full mb-1">
                <Phone className="h-6 w-6 text-chalet-gold-400" />
              </div>
              <span className="text-xs text-gray-300">
                {getTranslatedItem(footerItems.call.key, footerItems.call.default)}
              </span>
            </a>

            <a href="mailto:pavel.maxim@tannenboden.com" className="flex flex-col items-center">
              <div className="bg-chalet-gold-400/20 p-4 rounded-full mb-1">
                <Mail className="h-6 w-6 text-chalet-gold-400" />
              </div>
              <span className="text-xs text-gray-300">
                {getTranslatedItem(footerItems.email.key, footerItems.email.default)}
              </span>
            </a>

            <a
              href={getGoogleMapsDirectionsUrl(TANNENBODEN_ADDRESS)}
              target="_blank"
              rel="noopener noreferrer"
              className="flex flex-col items-center"
            >
              <div className="bg-chalet-gold-400/20 p-4 rounded-full mb-1">
                <MapPin className="h-6 w-6 text-chalet-gold-400" />
              </div>
              <span className="text-xs text-gray-300">
                {translate('footer.location', 'Standort')}
              </span>
            </a>
          </div>

          {/* Essential navigation - large touch targets */}
          <div className="flex flex-wrap justify-center gap-x-5 gap-y-3 text-center mb-6">
            <Link href={convertLegacyUrl("/", language)} className="text-gray-300 text-sm px-2 py-1">
              {getTranslatedItem(footerItems.home.key, footerItems.home.default)}
            </Link>
            <Link href={convertLegacyUrl("/engelberg-accommodation/chalet", language)} className="text-gray-300 text-sm px-2 py-1">
              {getTranslatedItem(footerItems.chalet.key, footerItems.chalet.default)}
            </Link>
            <Link href={convertLegacyUrl("/engelberg-accommodation/booking/availability", language)} className="text-gray-300 text-sm px-2 py-1">
              {getTranslatedItem(footerItems.book.key, footerItems.book.default)}
            </Link>
          </div>

          {/* Minimal branding and copyright */}
          <div className="text-center">
            <Image
              src="/icons/icon-72x72.webp"
              alt="Tannenboden"
              width={40}
              height={40}
              className="rounded-full inline-block"
            />
            <p className="text-xs text-gray-400 mt-3">
              {getTranslatedItem(footerItems.copyright.key, footerItems.copyright.default).replace('{year}', new Date().getFullYear().toString())}
            </p>
          </div>
        </div>

        {/* Desktop view - standard footer (hidden on mobile) */}
        <div className="hidden md:block max-w-7xl mx-auto py-8 px-4">
          <div className="flex justify-between items-center mb-8">
            <div className="flex items-center gap-4">
              <Image
                src="/icons/icon-72x72.webp"
                alt="Tannenboden Chalet"
                width={60}
                height={60}
                className="rounded-full"
              />
              <div>
                <h2 className="text-xl font-bold text-chalet-gold-400">
                  {translate('chalet.name', 'Tannenboden Chalet Engelberg')}
                </h2>
                <p className="text-sm text-gray-300">
                  {translate('chalet.description', 'Traditionelles Schweizer Chalet in Engelberg')}
                </p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 border-t border-gray-700/40 pt-6">
            {/* Contact section */}
            <div>
              <h3 className="text-chalet-gold-400 font-bold mb-3">
                {getTranslatedItem(footerItems.contact.key, footerItems.contact.default)}
              </h3>
              <ul className="space-y-2">
                <li>
                  <a href="mailto:pavel.maxim@tannenboden.com" className="text-gray-300 text-sm">
                    pavel.maxim@tannenboden.com
                  </a>
                </li>
                <li>
                  <a href="tel:+41787460447" className="text-gray-300 text-sm">
                    +41 78 746 04 47
                  </a>
                </li>
                <li>
                  <a
                    href={getGoogleMapsDirectionsUrl(TANNENBODEN_ADDRESS)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gray-300 text-sm hover:text-chalet-gold-400 flex items-center gap-1"
                  >
                    <MapPin size={14} className="inline-block" />
                    Sattelboden 1, Engelberg
                  </a>
                </li>
              </ul>
            </div>

            {/* Quick links */}
            <div>
              <h3 className="text-chalet-gold-400 font-bold mb-3">
                {getTranslatedItem(footerItems.quickLinks.key, footerItems.quickLinks.default)}
              </h3>
              <ul className="space-y-2">
                <li>
                  <Link href={convertLegacyUrl("/", language)} className="text-gray-300 text-sm">
                    {getTranslatedItem(footerItems.home.key, footerItems.home.default)}
                  </Link>
                </li>
                <li>
                  <Link href={convertLegacyUrl("/engelberg-accommodation/chalet", language)} className="text-gray-300 text-sm">
                    {getTranslatedItem(footerItems.chalet.key, footerItems.chalet.default)}
                  </Link>
                </li>
                <li>
                  <Link href={convertLegacyUrl("/engelberg-accommodation/booking/availability", language)} className="text-gray-300 text-sm">
                    {getTranslatedItem(footerItems.availability.key, footerItems.availability.default)}
                  </Link>
                </li>
                <li>
                  <Link href={convertLegacyUrl("/engelberg-accommodation/attractions", language)} className="text-gray-300 text-sm">
                    {getTranslatedItem(footerItems.attractions.key, footerItems.attractions.default)}
                  </Link>
                </li>
              </ul>
            </div>

            {/* Desktop-only additional links */}
            <div className="hidden lg:block">
              <h3 className="text-chalet-gold-400 font-bold mb-3">
                {getTranslatedItem(footerItems.activities.key, footerItems.activities.default)}
              </h3>
              <ul className="space-y-2">
                <li>
                  <Link href={convertLegacyUrl("/engelberg-accommodation/activities/summer", language)} className="text-gray-300 text-sm">
                    {getTranslatedItem(footerItems.summerActivities.key, footerItems.summerActivities.default)}
                  </Link>
                </li>
                <li>
                  <Link href={convertLegacyUrl("/engelberg-accommodation/ski-chalet", language)} className="text-gray-300 text-sm">
                    {getTranslatedItem(footerItems.skiing.key, footerItems.skiing.default)}
                  </Link>
                </li>
                <li>
                  <Link href={convertLegacyUrl("/engelberg-accommodation/family-accommodation", language)} className="text-gray-300 text-sm">
                    {getTranslatedItem(footerItems.familyAdventures.key, footerItems.familyAdventures.default)}
                  </Link>
                </li>
              </ul>
            </div>

            {/* Desktop-only additional info */}
            <div className="hidden lg:block">
              <h3 className="text-chalet-gold-400 font-bold mb-3">
                {getTranslatedItem(footerItems.followUs.key, footerItems.followUs.default)}
              </h3>
              <a
                href="https://facebook.com/tannenboden"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-300 text-sm hover:text-chalet-gold-400"
              >
                Facebook
              </a>
            </div>
          </div>

          <div className="mt-8 pt-4 border-t border-gray-700/40 text-center">
            <p className="text-xs text-gray-400">
              {getTranslatedItem(footerItems.copyright.key, footerItems.copyright.default).replace('{year}', new Date().getFullYear().toString())}
            </p>
          </div>
        </div>
      </footer>
    </>
  );
};

export default Footer;